import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { insights } from "@/lib/data/insights";
import useSEO from "@/hooks/useSEO";
import { withCanadaKeywords } from "@/lib/data/canada-keywords";

export default function Insights() {
  useSEO({
    title: "Insights — Manitoba Security Services",
    description:
      "Field notes, procurement guides, and technical explainers on mining security, fire watch, construction site protection, executive protection, and the operating standards behind them.",
    keywords: withCanadaKeywords([
      "security insights Manitoba",
      "security industry blog Canada",
      "Manitoba security guides",
      ...insights.flatMap((i) => i.keywords).slice(0, 20),
    ]),
    canonical:
      typeof window !== "undefined" ? window.location.origin + "/insights" : "/insights",
  });

  return (
    <div className="bg-[#050505] pt-40 md:pt-48">
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-16 md:pb-24 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm">Index — 08</p>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h1
            data-testid="insights-title"
            className="font-display font-black uppercase tracking-tightest leading-[0.88] text-white text-5xl md:text-7xl lg:text-8xl"
          >
            Field{" "}
            <span className="font-editorial italic font-normal text-[#a0a0a0]">
              notes.
            </span>
          </h1>
          <p className="mt-8 font-body text-base md:text-lg text-[#a0a0a0] max-w-2xl leading-relaxed">
            Working notes from the operating floor — procurement guides,
            technical explainers, and field lessons across every discipline
            Manitoba Security Services delivers.
          </p>
        </div>
      </section>

      <section className="border-t border-[#1e1e1e]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          {insights.map((a, i) => (
            <motion.div
              key={a.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.03, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link
                to={`/insights/${a.slug}`}
                data-testid={`insight-${a.slug}`}
                className="group grid grid-cols-12 gap-4 items-baseline border-b border-[#1e1e1e] py-8 md:py-10 hover:border-[#FF3B30] transition-colors duration-500"
              >
                <div className="col-span-2 md:col-span-1 text-[10px] md:text-xs font-body uppercase tracking-[0.14em] text-[#6a6a6a] group-hover:text-[#FF3B30] transition-colors">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-8 md:col-span-9">
                  <p className="font-editorial italic text-[#FF3B30] text-xs md:text-sm mb-2">
                    {a.category} · {a.readTime}
                  </p>
                  <h2 className="font-display font-black uppercase tracking-tight text-white text-xl md:text-3xl lg:text-4xl leading-tight group-hover:translate-x-2 transition-transform duration-500">
                    {a.title}
                  </h2>
                  <p className="mt-3 font-editorial italic text-[#a0a0a0] text-base md:text-lg max-w-2xl">
                    {a.excerpt}
                  </p>
                </div>
                <span className="col-span-2 flex justify-end">
                  <ArrowUpRight
                    className="text-white/60 group-hover:text-[#FF3B30] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
                    size={22}
                  />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
